import React, { useState, useCallback, useEffect } from 'react';
import { Container, Content, Title, Wrapper } from './styles';
import { Row, Col } from '../../components/GRID';
import { Table } from '../../components/UI/Table';

// MODELS
import { Filter } from '../../models/Filter';
import { Proposal } from '../../models/Proposal';

// CONTEXT
import { useProposal } from '../../hooks/AppContext';
import { useAuth } from '../../hooks/auth';

// SERVICES
import ProposalService from '../../services/ProposalService';

// UTILS
import { isRegularTerm, changeColumns } from '../../utils/filter';
import { getStatusOptionsForUser } from '../../utils/masterPayrollLoanFilter';
import logger from '../../utils/logger.utils';
import { isCancellationError } from '../../axios/requestCancelation';


const Manage: React.FC = () => {
  const {
    changeTableConfiguration,
    getPageSize,
    pageSize,
    setPageSize,
    setTableFilters,
    setTableSearch,
    tableConfiguration,
    tableFilters,
    tableSearch,
  } = useProposal();

  const { user } = useAuth();
  const [data, setData] = useState({});
  const [meta, setMeta] = useState({
    hasNextPage: true,
    hasPreviousPage: false,
    pageNumber: 0,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  });

  const [loading, setLoading] = useState(true);

  const handleLoadData = useCallback(async () => {
    const newData: Proposal[] = [];

    await ProposalService.fetchAll(tableFilters.getFilters())
      .then((response) => {
        if (!response) return;
        const { data: items } = response;
        if (!items) return;
        items.data.map((v: any) => {
          newData.push(new Proposal(v));
        });
        setData(newData);
        setMeta(items.meta);

        if (isRegularTerm(tableFilters?.searchTerm)) {
          const items = [...tableConfiguration];
          changeTableConfiguration(changeColumns(items));
        }

        setLoading(false);
      })
      .catch((error: any) => {
        if (isCancellationError(error)) {
          return;
        }
        logger.error('Error loading data');
      });
  }, [meta, tableFilters]);

  const handleReload = useCallback(() => {
    handleLoadData();
  }, [meta]);

  const handleSearch = useCallback(
    (value) => {
      setTableFilters(new Filter(tableFilters).set('searchTerm', value).set('pageNumber', 0));
    },
    [tableFilters],
  );

  const handleChangePage = useCallback(
    (pageNumber: number) => {
      setTableFilters(new Filter(tableFilters).set('pageNumber', pageNumber));
    },
    [tableFilters, meta],
  );
  const handlePageSize = useCallback(
    (pageSizeParam: number) => {
      setPageSize(getPageSize(pageSizeParam));
      setTableFilters(new Filter(tableFilters).set('pageSize', pageSizeParam).set('pageNumber', 0));
    },
    [tableFilters],
  );

  const handleOrdering = useCallback(
    (column: string, direction: string) => {
      setTableFilters(new Filter(tableFilters).set('orderField', column).set('orderType', direction));
    },
    [tableFilters],
  );

  const handleApplyFilter = useCallback(
    (convenant: any, status: any) => {
      setTableFilters(new Filter(tableFilters).set('convenant', convenant).set('status', status).set('pageNumber', 0));
    },
    [tableFilters],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      handleLoadData();
    };

    if (Object.keys(tableFilters).length === 0) {
      // Get initial status options based on user profile
      const statusOptions = getStatusOptionsForUser(user);
      const activeStatuses = statusOptions.filter((option: any) => option.active);

      // Initialize filter with the correct status values for the user
      const initialFilter = new Filter();
      initialFilter.set('status', activeStatuses);
      setTableFilters(initialFilter);
    } else {
      fetchData();
    }
  }, [tableFilters]);

  return (
    <Container>
      <Content>
        <Row centralized>
          <Col sm={18} md={18} lg={18}>
            <Title>Gerenciamento de propostas</Title>
          </Col>
        </Row>
        <Row>
          <Col sm={18} md={18} lg={18}>
            <Wrapper>
              <Table
                loading={loading}
                data={data}
                meta={meta}
                onReload={handleReload}
                onChangePage={handleChangePage}
                onChangePageSize={handlePageSize}
                onOrdering={handleOrdering}
                onSearch={handleSearch}
                onApplyFilter={handleApplyFilter}
                tableSearch={tableSearch}
                setTableSearch={setTableSearch}
                pageSize={pageSize}
              />
            </Wrapper>
          </Col>
        </Row>
      </Content>
    </Container>
  );
};
export default Manage;
